"use client"

import { useState, useRef, useEffect } from "react"
import Image from "next/image"
import { m, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"
import { useTranslations } from "@/lib/intl/use-translations"
import { PackageIcon } from "@/components/ui/package-icons"
import { ComingSoonPill } from "@/components/ui/coming-soon-pill"
import { blobMediaUrl } from "@/lib/blob-url"

interface Package {
  id: string
  label: string
  /** Renders a "Bald verfügbar" pill and, when any package sets it, drives the
   *  inline-vs-"Weitere" split (coming-soon packages move into the dropdown). */
  comingSoon?: boolean
}

/** Main package IDs that get top-level chips (not in the dropdown) */
const MAIN_PACKAGE_IDS = new Set(["core", "advanced", "pro", "blood"])

/** Map package ID → PackageIcon variant for the colored circle */
const ICON_VARIANT: Record<string, "green" | "blue" | "pink"> = {
  core: "green",
  advanced: "blue",
  pro: "pink",
}

/** Blood test uses a photo instead of an icon */
const BLOOD_IMAGE = blobMediaUrl("/images/homepage/service-blood-cells.jpeg")

interface CategoryFilterProps {
  packages: Package[]
  activePackage: string | null
  onSelectPackage: (id: string | null) => void
  searchQuery: string
  onSearchChange: (query: string) => void
  resultCount: number
  allLabel?: string
  searchPlaceholder?: string
  resultLabel?: string
  /** Label for the addon dropdown trigger (defaults to "Add-ons") */
  addonLabel?: string
  /** Fired when the user presses Enter in the search input (optional). */
  onSearchSubmit?: () => void
  /** Rendered directly under the search input, above the filter chips. Used by
   *  the listings to dock the AI answer lane to the search box (Layout A). */
  belowSearch?: React.ReactNode
}

export function CategoryFilter({
  packages,
  activePackage,
  onSelectPackage,
  searchQuery,
  onSearchChange,
  resultCount,
  allLabel = "All",
  searchPlaceholder = "Search...",
  resultLabel,
  addonLabel = "Add-ons",
  onSearchSubmit,
  belowSearch,
}: CategoryFilterProps) {
  const ut = useTranslations("ui")
  const displayResultLabel =
    resultLabel ?? `${resultCount} result${resultCount !== 1 ? "s" : ""}`

  // When any package is flagged `comingSoon` (DE: imaging isn't bookable yet),
  // split by that flag — bookable packages inline, coming-soon ones in the
  // "Weitere" overflow. Otherwise fall back to the fixed main-package set.
  const usesComingSoon = packages.some((p) => p.comingSoon)
  const mainPackages = usesComingSoon
    ? packages.filter((p) => !p.comingSoon)
    : packages.filter((p) => MAIN_PACKAGE_IDS.has(p.id))
  const addonPackages = usesComingSoon
    ? packages.filter((p) => p.comingSoon)
    : packages.filter((p) => !MAIN_PACKAGE_IDS.has(p.id))

  const [addonOpen, setAddonOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedAddon = addonPackages.find((p) => p.id === activePackage)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setAddonOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  function handleAddonSelect(id: string) {
    onSelectPackage(activePackage === id ? null : id)
    setAddonOpen(false)
  }

  return (
    <div
      className="bg-background/95 sticky top-[74px] z-30 py-4 backdrop-blur-md"
      role="toolbar"
      aria-label="Filter biomarkers"
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div className="flex flex-col gap-3">
          {/* Search — full-width row on top, above the filter chips */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <svg
                className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="search"
                aria-label="Search biomarkers and conditions"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    onSearchSubmit?.()
                  }
                }}
                /* type-body-lg = 16px on mobile so iOS doesn't auto-zoom (and
                   shift the page + spawn a horizontal scrollbar) on focus;
                   md:type-body-md keeps the smaller 14px look on desktop. */
                className="type-body-lg md:type-body-md text-aubergine-900 focus-visible:ring-aubergine-200 w-full rounded-full bg-white py-1.5 pr-3 pl-9 shadow-sm transition-shadow outline-none placeholder:text-neutral-400 focus-visible:ring-2"
              />
            </div>
            <span className="hidden text-sm whitespace-nowrap text-neutral-400 md:block">
              {displayResultLabel}
            </span>
          </div>

          {/* AI answer lane — docked directly under the search input */}
          {belowSearch}

          {/* Package pills + addon dropdown */}
          <div className="flex items-center gap-2">
            {/* Scrollable main chips */}
            <div className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-2">
              <button
                type="button"
                aria-pressed={activePackage === null}
                onClick={() => onSelectPackage(null)}
                className={cn(
                  "flex-shrink-0 cursor-pointer rounded-full border-[1.5px] px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-all duration-200",
                  activePackage === null
                    ? "border-aubergine-800 bg-aubergine-800 text-white"
                    : "hover:border-aubergine-400 hover:bg-aubergine-100 border-neutral-200 bg-white text-neutral-600"
                )}
              >
                {allLabel}
              </button>

              {/* Main package chips with icons */}
              {mainPackages.map((pkg) => {
                const isActive = activePackage === pkg.id
                const variant = ICON_VARIANT[pkg.id]
                const isBlood = pkg.id === "blood"
                // Icon chips (core/advanced/pro/blood) get the tight left inset
                // that seats the leading disc; text-only chips (e.g. the blood
                // add-ons promoted into the main row on DE) use symmetric
                // padding so the label sits centred like the "All" chip instead
                // of hugging the left edge.
                const hasIcon = Boolean(variant) || isBlood

                return (
                  <button
                    key={pkg.id}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => onSelectPackage(isActive ? null : pkg.id)}
                    className={cn(
                      "flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-full border-[1.5px] text-sm font-medium whitespace-nowrap transition-all duration-200",
                      hasIcon ? "py-1 pr-4 pl-1" : "px-4 py-1.5",
                      isActive
                        ? "border-aubergine-800 bg-aubergine-800 text-white"
                        : "hover:border-aubergine-400 hover:bg-aubergine-100 border-neutral-200 bg-white text-neutral-600"
                    )}
                  >
                    {variant && (
                      <PackageIcon
                        variant={variant}
                        size="sm"
                        className="!h-[26px] !w-[26px] !rounded-full"
                      />
                    )}
                    {isBlood && (
                      <Image
                        src={BLOOD_IMAGE}
                        alt=""
                        width={26}
                        height={26}
                        className="h-[26px] w-[26px] flex-shrink-0 rounded-full object-cover"
                      />
                    )}
                    {pkg.label}
                  </button>
                )
              })}
            </div>

            {/* Divider + addon dropdown — outside scroll container so panel isn't clipped */}
            {addonPackages.length > 0 && (
              <>
                <div className="h-6 w-px flex-shrink-0 bg-neutral-200" />
                <div ref={dropdownRef} className="relative flex-shrink-0 pb-2">
                  <button
                    type="button"
                    aria-expanded={addonOpen}
                    aria-haspopup="listbox"
                    onClick={() => setAddonOpen((prev) => !prev)}
                    className={cn(
                      "flex flex-shrink-0 cursor-pointer items-center gap-1.5 rounded-full border-[1.5px] px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-all duration-200",
                      selectedAddon
                        ? "border-aubergine-800 bg-aubergine-800 text-white"
                        : addonOpen
                          ? "bg-aubergine-50 text-aubergine-800 border-neutral-300"
                          : "hover:border-aubergine-400 hover:bg-aubergine-100 border-neutral-200 bg-white text-neutral-600"
                    )}
                  >
                    {selectedAddon?.label ?? addonLabel}
                    <m.svg
                      animate={{ rotate: addonOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      className="flex-shrink-0"
                    >
                      <path
                        d="M3 5.5L8 10.5L13 5.5"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </m.svg>
                  </button>

                  {/* safe-animate-presence: conditional-mount — gated by `addonOpen=true` */}
                  <AnimatePresence>
                    {addonOpen && (
                      <m.div
                        initial={{ opacity: 0, y: -8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{
                          duration: 0.25,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                        className="absolute right-0 z-50 mt-2 min-w-[220px] overflow-hidden rounded-[12px] border border-neutral-100 bg-white shadow-lg md:right-auto md:left-0"
                        role="listbox"
                      >
                        <ul
                          className="overflow-y-auto py-1.5"
                          style={{
                            maxHeight: 340,
                            scrollbarWidth: "thin",
                            scrollbarColor: "rgb(229 229 229) transparent",
                          }}
                        >
                          {addonPackages.map((addon) => {
                            const isSelected = addon.id === activePackage
                            return (
                              <li
                                key={addon.id}
                                role="option"
                                aria-selected={isSelected}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleAddonSelect(addon.id)}
                                className={cn(
                                  "flex cursor-pointer items-center justify-between gap-2 px-4 py-2.5 text-sm transition-colors",
                                  isSelected
                                    ? "bg-aubergine-50 text-aubergine-700 font-medium"
                                    : "text-neutral-700 hover:bg-neutral-50"
                                )}
                              >
                                <span>{addon.label}</span>
                                {addon.comingSoon && (
                                  <ComingSoonPill label={ut("comingSoon")} />
                                )}
                              </li>
                            )
                          })}
                        </ul>
                      </m.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
